import React, { useEffect, useState } from 'react';
import { Box, ModuleSection, ResponsiveImage } from '@/components';
import { ImageModel, ModuleBaseModel, ModuleSectionPropsModel } from '@/types';
import { colorFromBgColor } from '@/utils';
import { fadeIn } from '@/animations';
import classNames from 'classnames';
import tailwindConfig from '@/tailwind.screens';

type MultipleCardContentModel = ModuleBaseModel<
  {
    cards: {
      image?: ImageModel;
      subtitle?: string;
      title?: string;
      text?: string;
      backgroundColour?: string;
    }[];
    cardsInRow: number;
    cardWidth: number;
  } & ModuleSectionPropsModel
>;

const MultipleCardContent = (data: MultipleCardContentModel) => {
  const { cards, cardsInRow, cardWidth, noPaddingOnBottom, noPaddingOnTop } = data.props;

  const [computedCardWidth, setComputedCardWidth] = useState<number>(0);

  useEffect(() => {
    const onResize = () => {
      setComputedCardWidth(0);
      setTimeout(() => {
        setComputedCardWidth(window.innerWidth > parseInt(tailwindConfig.screens.lg, 10) ? cardWidth : 0);
      }, 50);
    };
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [cardWidth]);

  // Split cards into rows
  const rows = [];
  for (let i = 0; i < cards.length; i += cardsInRow) {
    rows.push(cards.slice(i, i + cardsInRow));
  }

  return (
    <ModuleSection data={data} className={classNames({ 'pt-3': noPaddingOnTop, 'pb-3': noPaddingOnBottom })}>
      <div className="container">
        <div className="flex flex-col items-center justify-center gap-4">
          {rows.map((row, rowIndex) => (
            <div
              key={`row-${rowIndex}`}
              className="flex w-full max-w-full flex-wrap justify-center"
              style={{
                width: `${computedCardWidth ? cardsInRow * computedCardWidth + 20 * cardsInRow + 'px' : 'auto'}`,
              }}
            >
              {row.map((card, cardIndex) => (
                <Box
                  key={`card-${rowIndex}-${cardIndex}`}
                  animation={fadeIn}
                  className="flex w-full flex-col py-3 sm:w-auto md:p-3 xl:grow"
                  style={
                    computedCardWidth
                      ? {
                          width: `${computedCardWidth}px`,
                        }
                      : undefined
                  }
                >
                  {card.image && (
                    <div className="w-full">
                      <ResponsiveImage
                        image={card.image}
                        widths={{
                          xs: Math.min(cardWidth || 330, 330),
                          sm: Math.min(cardWidth || 330, 330),
                          md: Math.min(cardWidth || 330, 330),
                          lg: cardWidth || 330,
                          xl: cardWidth || 330,
                          xxl: cardWidth || 440,
                        }}
                        className="h-auto w-full max-w-full"
                      />
                    </div>
                  )}

                  {(card.subtitle || card.title || card.text) && (
                    <div
                      className="flex w-full flex-col px-5 pt-5 pb-6"
                      style={{
                        color: colorFromBgColor(card.backgroundColour) || undefined,
                        backgroundColor: card.backgroundColour,
                      }}
                    >
                      {card.subtitle && <p className="mb-0 text-xs leading-[18px] font-semibold">{card.subtitle}</p>}
                      {card.title && <h4 className="mb-0 py-5 text-xl leading-[26px] font-normal">{card.title}</h4>}
                      {card.text && (
                        <div
                          className="[&_a]:text-report-link [&_a]:visited:text-report-visited-link mb-0 text-sm leading-relaxed [&_img]:h-[30px] [&_img]:w-[30px] [&_ol:last-child]:mb-0 [&_p:last-child]:mb-0 [&_ul:last-child]:mb-0"
                          dangerouslySetInnerHTML={{ __html: card.text }}
                        />
                      )}
                    </div>
                  )}
                </Box>
              ))}
            </div>
          ))}
        </div>
      </div>
    </ModuleSection>
  );
};

export default MultipleCardContent;
